import { Component, ChangeDetectorRef, OnInit, Inject } from "@angular/core";
import { ConnectionService } from "ng-connection-service";
import { SharedService } from './utils/services/shared.service';
import { SessionStorage } from 'ngx-webstorage';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Meta, Title } from "@angular/platform-browser";
import { DOCUMENT } from '@angular/common';
import { filter } from "rxjs/operators";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    standalone: false
})
export class AppComponent implements OnInit {
  isConnected = true;
  status = "ONLINE";
  public loading = true;
  @SessionStorage('auth') public user;
  
  constructor(
    public router: Router,
    private connectionService: ConnectionService,
    private changeDetector: ChangeDetectorRef,
    private sharedService: SharedService,
    private actRoute: ActivatedRoute,
    private metaService: Meta,
    private titleService: Title, // Added Title service
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isConnected = navigator.onLine;
  }

  title = "amazon-ihs-webapp";

  ngOnInit() {
    this.handleAuthRedirect();
    // Subscribe to loading state
    this.sharedService.getDisplay().subscribe((val: boolean) => {
      this.loading = val;
      this.changeDetector.detectChanges();
    });

    // Subscribe to router events for meta tag updates and title changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        setTimeout(() => {
          const url = event.urlAfterRedirects || event.url;
          this.updateMetaTags(url);
          this.updatePageTitle(url); // Added title update
        }, 100); // Increased timeout to ensure DOM is ready
      });

    // Set initial meta tags and title
    setTimeout(() => {
      this.updateMetaTags(this.router.url);
      this.updatePageTitle(this.router.url); // Added initial title update
    }, 100);
  }

  private handleAuthRedirect() {
    // Only redirect if not authenticated and not on login page or interest form
    // Uses window.location instead of this.router.url because the Router may not
    // have finished resolving the initial navigation yet when ngOnInit runs.
    const currentUrl = window.location.pathname;
    const isOnLoginPage = currentUrl === '/login' || currentUrl === '/';
    const isOnInterestForm = currentUrl.includes('/ihs-interest-form');

    if (!this.user && !isOnLoginPage && !isOnInterestForm) {
      this.router.navigateByUrl('/login');
    }
  }

  // Path without query string, fragment or trailing slash, e.g. '/ihs-interest-form/ar'
  private getPath(url: string): string {
    return url.split(/[?#]/)[0].replace(/\/+$/, '');
  }

  private isArabicInterestForm(url: string): boolean {
    return this.getPath(url) === '/ihs-interest-form/ar';
  }

  private updatePageTitle(url: string) {
    const newTitle = this.isArabicInterestForm(url)
      ? 'أمازون هب دليفري | التسجيل كشريك توصيل'
      : 'Amazon Hub Delivery';

    this.titleService.setTitle(newTitle);
    
    setTimeout(() => {
      this.document.title = newTitle;
      
      setTimeout(() => {
        if (this.document.title !== newTitle) {
          this.document.title = newTitle;
        }
      }, 100);
    }, 50);
  }

  updateMetaTags(url: string) {
    this.removeRobotsMetaTag();
    this.removeCanonicalLink(); // Remove existing canonical link
    this.removeHreflangLinks();
    this.metaService.removeTag("name='description'");

    // Check for interest form URL (handles various possible patterns)
    const isInterestFormPage = url.includes('/ihs-interest-form') ||
                              url.includes('/interest-form') ||
                              url === '/ihs-interest-form';
    const isArabicPage = this.isArabicInterestForm(url);
    const isEnglishPage = this.getPath(url) === '/ihs-interest-form';

    this.document.documentElement.setAttribute('lang', isArabicPage ? 'ar' : 'en');

    if (isInterestFormPage) {
      this.addRobotsMetaTag('index, follow');
      const currentDomain = window.location.hostname;
      const baseUrl = currentDomain === 'hub.aziro.app'
        ? 'https://hub.aziro.app/ihs-interest-form'
        : 'https://hub-delivery.aziro.app/ihs-interest-form';

      // Arabic page is self-canonical so it is indexed independently
      this.addCanonicalLink(isArabicPage ? `${baseUrl}/ar` : baseUrl);

      // Reciprocal hreflang between the English and Arabic versions
      if (isEnglishPage || isArabicPage) {
        this.addHreflangLink('en', baseUrl);
        this.addHreflangLink('ar', `${baseUrl}/ar`);
      }

      if (isArabicPage) {
        this.metaService.addTag({
          name: 'description',
          content: 'سجّل متجرك كشريك توصيل في برنامج أمازون هب دليفري. أدخل بياناتك الشخصية ومعلومات متجرك لتقديم طلب الانضمام إلى البرنامج.'
        });
      }
    } else {
      this.addRobotsMetaTag('noindex, nofollow');
    }
  }

  private removeHreflangLinks() {
    this.document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach(link => link.remove());
  }

  private addHreflangLink(lang: string, href: string) {
    const linkTag = this.document.createElement('link');
    linkTag.setAttribute('rel', 'alternate');
    linkTag.setAttribute('hreflang', lang);
    linkTag.setAttribute('href', href);
    this.document.head.appendChild(linkTag);
  }

  private removeRobotsMetaTag() {
    // Remove using Angular Meta service
    this.metaService.removeTag("name='robots'");
    
    // Also remove directly from DOM head as fallback
    const head = this.document.head;
    const existingMetaTag = head.querySelector('meta[name="robots"]');
    if (existingMetaTag) {
      head.removeChild(existingMetaTag);
    }
  }

  private addRobotsMetaTag(content: string) {
    try {
      this.metaService.addTag({ 
        name: 'robots', 
        content: content 
      });
      
      // Verify it was added to head, if not, use fallback method
      setTimeout(() => {
        const headMetaTag = this.document.head.querySelector('meta[name="robots"]');
        if (!headMetaTag) {
          this.addMetaTagDirectly(content);
        } 
      }, 10);
      
    } catch (error) {
      this.addMetaTagDirectly(content);
    }
  }

  private addMetaTagDirectly(content: string) {
    const head = this.document.head;
    
    // Remove any existing robots meta tag from head
    const existingMetaTag = head.querySelector('meta[name="robots"]');
    if (existingMetaTag) {
      head.removeChild(existingMetaTag);
    }
    
    // Create and add new meta tag directly to head
    const metaTag = this.document.createElement('meta');
    metaTag.setAttribute('name', 'robots');
    metaTag.setAttribute('content', content);
    
    // Insert at the beginning of head for better SEO
    if (head.firstChild) {
      head.insertBefore(metaTag, head.firstChild);
    } else {
      head.appendChild(metaTag);
    }
  }

  private removeCanonicalLink() {
    // Remove any existing canonical link from head
    const head = this.document.head;
    const existingCanonicalLink = head.querySelector('link[rel="canonical"]');
    if (existingCanonicalLink) {
      head.removeChild(existingCanonicalLink);
    }
  }

  private addCanonicalLink(href: string) {
    const head = this.document.head;
    
    // Create and add canonical link tag directly to head
    const linkTag = this.document.createElement('link');
    linkTag.setAttribute('rel', 'canonical');
    linkTag.setAttribute('href', href);
    
    // Insert at the beginning of head for better SEO
    if (head.firstChild) {
      head.insertBefore(linkTag, head.firstChild);
    } else {
      head.appendChild(linkTag);
    }
  }
}